import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import { useLocalization, useTranslation } from '@/i18n';
import { cx } from './utils';

type DndDemoItemKind = 'weapon' | 'gear';

type DndDemoItem = {
  id: string;
  nameKey: string;
  nameEn: string;
  nameRu: string;
  detailKey: string;
  detailEn: string;
  detailRu: string;
  damageKey: string;
  damageEn: string;
  damageRu: string;
  traitsKey: string;
  traitsEn: string;
  traitsRu: string;
  bulk: string;
  kind: DndDemoItemKind;
};

type DndDemoSlotId = 'main' | 'off' | 'utility';

type DndDemoSlot = {
  id: DndDemoSlotId;
  titleKey: string;
  titleEn: string;
  titleRu: string;
  hintKey: string;
  hintEn: string;
  hintRu: string;
  accepts: DndDemoItemKind[];
};

const DEMO_ITEMS: DndDemoItem[] = [
  {
    id: 'longsword',
    nameKey: 'ui.demo.dnd.item.longsword',
    nameEn: 'Longsword',
    nameRu: 'Длинный меч',
    detailKey: 'ui.demo.dnd.item.longswordDetail',
    detailEn: 'Melee, d8 slashing',
    detailRu: 'Ближнее, d8 рубящий',
    damageKey: 'ui.demo.dnd.item.longswordDamage',
    damageEn: '1d8 S',
    damageRu: '1d8 Р',
    traitsKey: 'ui.demo.dnd.item.longswordTraits',
    traitsEn: 'Versatile P',
    traitsRu: 'Универсальное К',
    bulk: '1',
    kind: 'weapon',
  },
  {
    id: 'shortbow',
    nameKey: 'ui.demo.dnd.item.shortbow',
    nameEn: 'Shortbow',
    nameRu: 'Короткий лук',
    detailKey: 'ui.demo.dnd.item.shortbowDetail',
    detailEn: 'Ranged, d6 piercing',
    detailRu: 'Дальнее, d6 колющий',
    damageKey: 'ui.demo.dnd.item.shortbowDamage',
    damageEn: '1d6 P',
    damageRu: '1d6 К',
    traitsKey: 'ui.demo.dnd.item.shortbowTraits',
    traitsEn: 'Deadly d10',
    traitsRu: 'Смертельное d10',
    bulk: '1',
    kind: 'weapon',
  },
  {
    id: 'rapier',
    nameKey: 'ui.demo.dnd.item.rapier',
    nameEn: 'Rapier',
    nameRu: 'Рапира',
    detailKey: 'ui.demo.dnd.item.rapierDetail',
    detailEn: 'Finesse, d6 piercing',
    detailRu: 'Точное, d6 колющий',
    damageKey: 'ui.demo.dnd.item.rapierDamage',
    damageEn: '1d6 P',
    damageRu: '1d6 К',
    traitsKey: 'ui.demo.dnd.item.rapierTraits',
    traitsEn: 'Finesse, Deadly d8',
    traitsRu: 'Точное, Смертельное d8',
    bulk: '1',
    kind: 'weapon',
  },
  {
    id: 'warhammer',
    nameKey: 'ui.demo.dnd.item.warhammer',
    nameEn: 'Warhammer',
    nameRu: 'Боевой молот',
    detailKey: 'ui.demo.dnd.item.warhammerDetail',
    detailEn: 'Melee, d8 bludgeoning',
    detailRu: 'Ближнее, d8 дробящий',
    damageKey: 'ui.demo.dnd.item.warhammerDamage',
    damageEn: '1d8 B',
    damageRu: '1d8 Д',
    traitsKey: 'ui.demo.dnd.item.warhammerTraits',
    traitsEn: 'Shove',
    traitsRu: 'Толчок',
    bulk: '1',
    kind: 'weapon',
  },
];

const DEMO_SLOTS: DndDemoSlot[] = [
  {
    id: 'main',
    titleKey: 'ui.demo.dnd.slot.main',
    titleEn: 'Main hand',
    titleRu: 'Основная рука',
    hintKey: 'ui.demo.dnd.slot.weaponHint',
    hintEn: 'Accepts one weapon',
    hintRu: 'Принимает одно оружие',
    accepts: ['weapon'],
  },
  {
    id: 'off',
    titleKey: 'ui.demo.dnd.slot.off',
    titleEn: 'Off hand',
    titleRu: 'Вторая рука',
    hintKey: 'ui.demo.dnd.slot.weaponHint',
    hintEn: 'Accepts one weapon',
    hintRu: 'Принимает одно оружие',
    accepts: ['weapon'],
  },
  {
    id: 'utility',
    titleKey: 'ui.demo.dnd.slot.utility',
    titleEn: 'Utility pouch',
    titleRu: 'Поясная сумка',
    hintKey: 'ui.demo.dnd.slot.noWeaponsHint',
    hintEn: 'Does not accept weapons',
    hintRu: 'Не принимает оружие',
    accepts: ['gear'],
  },
];

type DndDemoSlotsState = Record<DndDemoSlotId, string | null>;

function DraggableDemoItem({
  item,
  variant = 'row',
  tt,
}: {
  item: DndDemoItem;
  variant?: 'row' | 'slot';
  tt: (key: string, en: string, ru: string) => string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id });

  if (variant === 'slot') {
    return (
      <div
        ref={setNodeRef}
        className={cx('ui-dnd-demo__equipped-item', isDragging && 'ui-dnd-demo__item--dragging')}
        data-ui-dnd-item-id={item.id}
        {...attributes}
        {...listeners}
      >
        <span className="ui-dnd-demo__grip" aria-hidden="true">⠿</span>
        <span className="ui-dnd-demo__slot-name-line">
          <span className="ui-dnd-demo__slot-item-name">{tt(item.nameKey, item.nameEn, item.nameRu)}</span>
          <span className="ui-dnd-demo__slot-stats-inline">({tt(item.detailKey, item.detailEn, item.detailRu)})</span>
        </span>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={cx('ui-dnd-demo__row', isDragging && 'ui-dnd-demo__item--dragging')}
      data-ui-dnd-item-id={item.id}
      {...attributes}
      {...listeners}
    >
      <span className="ui-dnd-demo__grip" aria-hidden="true">⠿</span>
      <span className="ui-dnd-demo__row-name">{tt(item.nameKey, item.nameEn, item.nameRu)}</span>
      <span className="ui-dnd-demo__row-damage">{tt(item.damageKey, item.damageEn, item.damageRu)}</span>
      <span className="ui-dnd-demo__row-traits">{tt(item.traitsKey, item.traitsEn, item.traitsRu)}</span>
      <span className="ui-dnd-demo__row-bulk">{item.bulk}</span>
    </div>
  );
}

function DndDemoDropSlot({
  slot,
  item,
  draggingItem,
  tt,
}: {
  slot: DndDemoSlot;
  item: DndDemoItem | null;
  draggingItem: DndDemoItem | null;
  tt: (key: string, en: string, ru: string) => string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot:${slot.id}` });
  const acceptsDraggingItem = draggingItem ? slot.accepts.includes(draggingItem.kind) : false;
  const stateClass = draggingItem
    ? isOver
      ? acceptsDraggingItem ? 'ui-dnd-demo__slot--over' : 'ui-dnd-demo__slot--over-invalid'
      : acceptsDraggingItem ? 'ui-dnd-demo__slot--valid' : 'ui-dnd-demo__slot--invalid'
    : '';

  return (
    <section
      ref={setNodeRef}
      className={cx('ui-dnd-demo__slot', item && 'ui-dnd-demo__slot--filled', stateClass)}
      data-ui-dnd-slot-id={slot.id}
    >
      <span className="ui-dnd-demo__slot-type-label">{tt(slot.titleKey, slot.titleEn, slot.titleRu)}</span>
      {item ? (
        <DraggableDemoItem item={item} variant="slot" tt={tt} />
      ) : (
        <div className="ui-dnd-demo__empty">
          {tt('ui.demo.dnd.emptySlot', 'Drop item here', 'Перетащите предмет сюда')}
          <span>{tt(slot.hintKey, slot.hintEn, slot.hintRu)}</span>
        </div>
      )}
    </section>
  );
}

function DndDemoPool({
  items,
  isDragging,
  tt,
}: {
  items: DndDemoItem[];
  isDragging: boolean;
  tt: (key: string, en: string, ru: string) => string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'pool' });

  return (
    <section
      ref={setNodeRef}
      className={cx('ui-dnd-demo__pool', isDragging && 'ui-dnd-demo__pool--active', isDragging && isOver && 'ui-dnd-demo__pool--over')}
      data-ui-dnd-pool
    >
      <header className="ui-dnd-demo__pool-header">
        <strong>{tt('ui.demo.dnd.pool.title', 'Weapon rack', 'Стойка оружия')}</strong>
        <span>{tt('ui.demo.dnd.pool.hint', 'Accepts any number of items', 'Принимает любое число предметов')}</span>
      </header>
      <div className="ui-dnd-demo__table" role="table" aria-label={tt('ui.demo.dnd.pool.title', 'Weapon rack', 'Стойка оружия')}>
        <div className="ui-dnd-demo__table-head" role="row">
          <span aria-hidden="true" />
          <span>{tt('ui.demo.dnd.column.item', 'Item', 'Предмет')}</span>
          <span>{tt('ui.demo.dnd.column.damage', 'Damage', 'Урон')}</span>
          <span>{tt('ui.demo.dnd.column.traits', 'Traits', 'Свойства')}</span>
          <span>{tt('ui.demo.dnd.column.bulk', 'Bulk', 'Масса')}</span>
        </div>
        {items.map((item) => (
          <DraggableDemoItem key={item.id} item={item} tt={tt} />
        ))}
        {items.length === 0 ? (
          <div className="ui-dnd-demo__empty">
            {tt('ui.demo.dnd.emptyPool', 'Drag equipped items back here', 'Верните экипированные предметы сюда')}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function DndDemo() {
  const t = useTranslation();
  const { language } = useLocalization();
  const tt = (key: string, en: string, ru: string) => t(key, {}, language === 'ru' ? ru : en);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [poolItemIds, setPoolItemIds] = useState(() => DEMO_ITEMS.map((item) => item.id));
  const [slots, setSlots] = useState<DndDemoSlotsState>({ main: null, off: null, utility: null });
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);

  const itemsById = useMemo(() => new Map(DEMO_ITEMS.map((item) => [item.id, item])), []);
  const draggingItem = draggingItemId ? itemsById.get(draggingItemId) ?? null : null;
  const poolItems = poolItemIds.map((id) => itemsById.get(id)).filter((item): item is DndDemoItem => Boolean(item));
  const slotEntries = DEMO_SLOTS.map((slot) => ({
    slot,
    item: slots[slot.id] ? itemsById.get(slots[slot.id]!) ?? null : null,
  }));

  const findItemSlot = (itemId: string): DndDemoSlotId | null => (
    DEMO_SLOTS.find((slot) => slots[slot.id] === itemId)?.id ?? null
  );

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingItemId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const itemId = String(event.active.id);
    const item = itemsById.get(itemId);
    const overId = event.over?.id ? String(event.over.id) : null;
    setDraggingItemId(null);
    if (!item || !overId) return;

    const sourceSlot = findItemSlot(itemId);

    if (overId === 'pool') {
      setSlots((current) => sourceSlot ? { ...current, [sourceSlot]: null } : current);
      setPoolItemIds((current) => current.includes(itemId) ? current : [...current, itemId]);
      return;
    }

    if (!overId.startsWith('slot:')) return;
    const targetSlotId = overId.slice(5) as DndDemoSlotId;
    const targetSlot = DEMO_SLOTS.find((slot) => slot.id === targetSlotId);
    if (!targetSlot || !targetSlot.accepts.includes(item.kind)) return;
    if (sourceSlot === targetSlotId) return;

    const displacedItemId = slots[targetSlotId];
    setSlots((current) => {
      if (sourceSlot) {
        return {
          ...current,
          [sourceSlot]: displacedItemId ?? null,
          [targetSlotId]: itemId,
        };
      }
      return {
        ...current,
        [targetSlotId]: itemId,
      };
    });
    if (!sourceSlot) {
      setPoolItemIds((current) => {
        const withoutDragged = current.filter((id) => id !== itemId);
        return displacedItemId && displacedItemId !== itemId && !withoutDragged.includes(displacedItemId)
          ? [...withoutDragged, displacedItemId]
          : withoutDragged;
      });
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="ui-dnd-demo">
        <div className="ui-dnd-demo__slots">
          {slotEntries.map(({ slot, item }) => (
            <DndDemoDropSlot key={slot.id} slot={slot} item={item} draggingItem={draggingItem} tt={tt} />
          ))}
        </div>
        <DndDemoPool items={poolItems} isDragging={Boolean(draggingItem)} tt={tt} />
      </div>
      <DragOverlay dropAnimation={null}>
        {draggingItem ? (
          <div className="ui-dnd-demo__overlay">
            {tt(draggingItem.nameKey, draggingItem.nameEn, draggingItem.nameRu)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
